import React from 'react';
import { MessageSquare, Zap, Edit, Globe } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useScrollAnimation } from '../hooks/useScrollAnimation';
export function HowItWorks() {
  const {
    t
  } = useLanguage();
  const {
    ref: titleRef,
    isVisible: titleVisible
  } = useScrollAnimation();
  const steps = [{
    icon: MessageSquare,
    title: t.howItWorks.steps[0].title,
    desc: t.howItWorks.steps[0].desc,
    number: '01'
  }, {
    icon: Zap,
    title: t.howItWorks.steps[1].title,
    desc: t.howItWorks.steps[1].desc,
    number: '02'
  }, {
    icon: Edit,
    title: t.howItWorks.steps[2].title,
    desc: t.howItWorks.steps[2].desc,
    number: '03'
  }, {
    icon: Globe,
    title: t.howItWorks.steps[3].title,
    desc: t.howItWorks.steps[3].desc,
    number: '04'
  }];
  return <section className="py-24 bg-[#050505]" data-id="element-220">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-221">
        <div ref={titleRef} className={`text-center mb-16 animate-hidden animate-fade-up ${titleVisible ? 'animate-visible' : ''}`} data-id="element-222">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" data-id="element-223">
            {t.howItWorks.title}
          </h2>
          <p className="text-lg text-gray-400 max-w-2xl mx-auto" data-id="element-224">
            {t.howItWorks.subtitle}
          </p>
        </div>

        {/* Desktop View: Zigzag */}
        <div className="hidden md:block space-y-6" data-id="element-225">
          {steps.map((step, index) => <DesktopStep key={index} step={step} index={index} data-id="element-226" />)}
        </div>

        {/* Mobile View: Vertical Cards */}
        <div className="md:hidden space-y-4" data-id="element-227">
          {steps.map((step, index) => <MobileStep key={index} step={step} index={index} data-id="element-228" />)}
        </div>
      </div>
    </section>;
}
function DesktopStep({
  step,
  index
}: {
  step: any;
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useScrollAnimation({
    threshold: 0.2
  });
  const isEven = index % 2 === 0;
  return <div ref={ref} className={`flex w-full ${isEven ? 'justify-start' : 'justify-end'} animate-hidden ${isEven ? 'animate-slide-right' : 'animate-slide-left'} ${isVisible ? 'animate-visible' : ''}`} data-id="element-229">
      <div className="relative w-[80%] bg-[#0A0A0A] rounded-2xl p-8 border border-white/10 flex flex-row items-center gap-8 overflow-hidden hover:border-accent-blue/30 transition-colors duration-300" data-id="element-230">
        <div className="w-14 h-14 rounded-xl bg-accent-blue/10 flex items-center justify-center shrink-0 z-10" data-id="element-231">
          <step.icon className="w-7 h-7 text-accent-blue" data-id="element-232" />
        </div>
        <div className="flex-1 text-left z-10" data-id="element-233">
          <h3 className="text-xl font-bold text-white mb-2" data-id="element-234">{step.title}</h3>
          <p className="text-gray-400 text-sm leading-relaxed" data-id="element-235">{step.desc}</p>
        </div>
        <div className="absolute right-8 top-1/2 -translate-y-1/2 text-7xl font-bold text-white/[0.07] select-none pointer-events-none z-0" data-id="element-236">
          {step.number}
        </div>
      </div>
    </div>;
}
function MobileStep({
  step,
  index
}: {
  step: any;
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useScrollAnimation({
    threshold: 0.1
  });
  return <div ref={ref} className={`relative bg-[#0A0A0A] rounded-2xl p-5 border border-white/10 overflow-hidden animate-hidden animate-fade-up ${isVisible ? 'animate-visible' : ''}`} data-id="element-237">
      <div className="flex items-center gap-3 mb-3 relative z-10" data-id="element-238">
        <step.icon className="w-6 h-6 text-accent-blue flex-shrink-0" data-id="element-239" />
        <h3 className="text-lg font-bold text-white leading-tight" data-id="element-240">
          {step.title}
        </h3>
      </div>
      <p className="text-gray-400 text-sm leading-relaxed relative z-10 pr-8" data-id="element-241">
        {step.desc}
      </p>
      <div className="absolute right-2 top-1/2 -translate-y-1/2 text-6xl font-bold text-white/[0.07] select-none pointer-events-none z-0" data-id="element-242">
        {step.number}
      </div>
    </div>;
}